import React, { useCallback, useLayoutEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import useTranslationHook from "../../../../hooks/useTranslationHook";
import { updateBuyerRegistrationField } from "../../../../../redux/slice/buyerSlice";
import {
    BUYER_REGISTRATION_STEPS,
    Payment_Method,
    RegistrationFlowType,
} from "./constant";
import { useForm } from "react-hook-form";
import { initiatePurchase } from "../../../../../redux/action/paymentAction";
import { handleSiteLoader } from "../../../../../redux/action/authAction";
import useDidMountEffect from "../../../../hooks/useDidMountEffect";
import { useLocation } from "react-router-dom";
import { handleSelection } from "../../../../../redux/slice/liveAuctionSlice";
import { formatNumberByLocale } from "../../../../../helpers";

const RegistrationDepositPayment = () => {
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { t } = useTranslationHook();
    const registration_id = useSelector(
        (state) => state.buyer.buyerRegistration.registration_id
    );
    const lots = useSelector((state) => state.liveAuction.lots);
    const bid_transaction_id = useSelector(
        (state) => state.liveAuction.bid_transaction_id
    );
    const selectedLot = useSelector((state) => state.liveAuction.selectedLot);
    const registrationType = useSelector(
        (state) => state.buyer.buyerRegistration.registrationType
    );
    const deposit_amount = useSelector(
        (state) => state.liveAuction.deposit_amount
    );
    const lang = useSelector((state) => state.translation.lang);

    const setBuyerField = useCallback(
        (field, value) =>
            dispatch(updateBuyerRegistrationField({ field, value })),
        []
    );
    const property_id = useSelector(
        (state) => state.buyer.buyerRegistration.property_id
    );
    const paymentId = useSelector(
        (state) => state.payment.paymentId,
        shallowEqual
    );
    const paymentUrl = useSelector(
        (state) => state.payment.paymentUrl,
        shallowEqual
    );

    const {
        depositAmount,
        currentPathName,
        selectedSessionID,
        lotID,
        isBidLimitIncrease,
    } = state || {};

    const BidMultiplier = lots?.data?.[0]?.bid_limit_multiplier ?? 10;
    const currentBidLimit = selectedLot?.available_bid_limit ?? 0;
    const previouslyPaid = Math.round(currentBidLimit / BidMultiplier);
    const totalHoldAmount = previouslyPaid + deposit_amount;

    useDidMountEffect(() => {
        if (!paymentUrl || !paymentId) return;
        dispatch(handleSiteLoader(true));
        requestAnimationFrame(() => {
            window.location.href = `${paymentUrl}?PaymentID=${paymentId}`;
        });
        return () => {
            dispatch(handleSiteLoader(false));
            if (!isBidLimitIncrease) {
                dispatch(handleSelection({ deposit_amount: 0 }));
            }
        };
    }, [paymentUrl, paymentId]);

    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm({
        defaultValues: { paymentMethod: Payment_Method.CARD },
    });

    const handleProceedPayment = ({ paymentMethod }) => {
        if (!paymentMethod) return;
        if (paymentMethod === Payment_Method.CARD) {
            dispatch(
                initiatePurchase({
                    udf6: isBidLimitIncrease ? 7 : 1,
                    udf7: "no",
                    property_id,
                    amount: selectedSessionID ? deposit_amount : depositAmount,
                    purchaseType: "normal",
                    payment_type: isBidLimitIncrease
                        ? "limit_increment"
                        : selectedSessionID
                          ? "session_registration"
                          : "registration",
                    ...(registration_id && {
                        udf9: isBidLimitIncrease
                            ? bid_transaction_id
                            : registration_id,
                    }),
                    udf8:
                        registrationType === RegistrationFlowType.SELF ? 1 : 2,
                    ...(selectedSessionID && { selectedSessionID }),
                    ...(isBidLimitIncrease && { isBidLimitIncrease }),
                })
            );
            sessionStorage.setItem(
                "bidder_reg_type",
                registrationType === RegistrationFlowType.SELF ? 1 : 2
            );
            if (lotID) sessionStorage.setItem("selectedLotID", `${lotID}`);
            if (property_id) sessionStorage.setItem("property_id", property_id);
            if (selectedSessionID)
                sessionStorage.setItem(
                    "selectedSessionID",
                    `${selectedSessionID}`
                );
            sessionStorage.setItem("currentPathName", currentPathName);
            if (isBidLimitIncrease)
                sessionStorage.setItem("isBidLimitIncrease", "true");
        }
    };

    return (
        <div>
            {selectedSessionID ? (
                <div
                    className={
                        isBidLimitIncrease
                            ? "step-3 bid-increase-wrap"
                            : "step-4"
                    }
                >
                    <section className="lot-progress">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-lg-8 col-md-10 col-sm-12">
                                    <h5>
                                        <span>
                                            {isBidLimitIncrease
                                                ? t("STEP 2/2")
                                                : "STEP 4/4"}
                                            <circle></circle>
                                            {isBidLimitIncrease
                                                ? t("Deposit payment")
                                                : "Registering for Live Auction Name"}
                                        </span>
                                        {isBidLimitIncrease
                                            ? t("Deposit Payment")
                                            : "Deposit & Bidding Limit"}
                                        <em>
                                            {isBidLimitIncrease
                                                ? t(
                                                      "Deposit payment is required to register for this auction"
                                                  )
                                                : "Adjust your deposit to increase your bidding power."}
                                        </em>
                                    </h5>
                                    {isBidLimitIncrease ? (
                                        <ul className="two-step">
                                            <li className="step active"></li>
                                            <li className="step"></li>
                                        </ul>
                                    ) : (
                                        <ul>
                                            <li className="step active"></li>
                                            <li className="step active"></li>
                                            <li className="step active"></li>
                                            <li className="step"></li>
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="lot-form">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-lg-8 col-md-10 col-sm-12">
                                    <div className="register-for-someone">
                                        {isBidLimitIncrease ? (
                                            /* ── Increase flow payment card ── */
                                            <div className="increase-payment-card">
                                                <div className="">
                                                    <div className="payment-row">
                                                        <div className="payment-row-label">
                                                            <h6 className="label-main">
                                                                {t("Pay Now")}
                                                            </h6>
                                                        </div>
                                                        <div className="payment-row-amount">
                                                            <h6 className="amount-main">
                                                                <em className="dirham">
                                                                    D
                                                                </em>{" "}
                                                                {formatNumberByLocale(
                                                                    deposit_amount,
                                                                    lang
                                                                )}
                                                            </h6>
                                                        </div>
                                                    </div>
                                                    <div className="payment-row">
                                                        <div className="payment-row-label">
                                                            <span className="label-sub">
                                                                {t(
                                                                    "Previously Paid"
                                                                )}
                                                            </span>
                                                        </div>
                                                        <div className="payment-row-amount">
                                                            <span className="amount-sub">
                                                                <em className="dirham">
                                                                    D
                                                                </em>{" "}
                                                                {t("Amount", {
                                                                    amount: formatNumberByLocale(
                                                                        previouslyPaid,
                                                                        lang
                                                                    ),
                                                                })}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="payment-divider"></div>
                                                <div className="payment-row total-row">
                                                    <div className="payment-row-label">
                                                        <h6 className="label-main">
                                                            {t(
                                                                "Total Hold Amount"
                                                            )}
                                                        </h6>
                                                        <span className="label-sub">
                                                            {t(
                                                                "Fully refundable if you don't win"
                                                            )}
                                                        </span>
                                                    </div>
                                                    <div className="payment-row-amount">
                                                        <h6 className="amount-main">
                                                            <em className="dirham">
                                                                D
                                                            </em>{" "}
                                                            {formatNumberByLocale(
                                                                totalHoldAmount,
                                                                lang
                                                            )}
                                                        </h6>
                                                        <span className="badge badge-success">
                                                            {t("Refundable")}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="payment-divider"></div>
                                                <div className="payment-row payment-method-row">
                                                    <h6 className="label-main">
                                                        {t("Payment Method")}
                                                    </h6>
                                                    <ul className="payment-mode">
                                                        <li>
                                                            <img
                                                                src="/img/applepay.png"
                                                                alt="Apple Pay"
                                                            />
                                                        </li>
                                                        <li>
                                                            <img
                                                                src="/img/googlepay.png"
                                                                alt="Google Pay"
                                                            />
                                                        </li>
                                                        <li>
                                                            <img
                                                                src="/img/visa-logo.png"
                                                                alt="Visa"
                                                            />
                                                        </li>
                                                        <li>
                                                            <img
                                                                src="/img/mastercard.png"
                                                                alt="Mastercard"
                                                            />
                                                        </li>
                                                        <li className="amex">
                                                            <img
                                                                src="/img/amex.png"
                                                                alt="Amex"
                                                            />
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="lot-payment">
                                                <div className="block">
                                                    <h6>
                                                        {t(
                                                            "Deposit Hold Amount"
                                                        )}
                                                        <span>
                                                            {t(
                                                                "Fully refundable if you don't win"
                                                            )}
                                                        </span>
                                                    </h6>
                                                    <div className="price">
                                                        <h5>
                                                            <em className="dirham">
                                                                D
                                                            </em>{" "}
                                                            {deposit_amount}
                                                        </h5>
                                                        <div className="badge badge-success">
                                                            {t("Refundable")}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="block">
                                                    <h6>
                                                        {t("Payment Method")}
                                                    </h6>
                                                    <ul className="payment-mode">
                                                        <li>
                                                            <img
                                                                src="/img/applepay.png"
                                                                alt=""
                                                            />
                                                        </li>
                                                        <li>
                                                            <img
                                                                src="/img/googlepay.png"
                                                                alt=""
                                                            />
                                                        </li>
                                                        <li>
                                                            <img
                                                                src="/img/visa-logo.png"
                                                                alt=""
                                                            />
                                                        </li>
                                                        <li>
                                                            <img
                                                                src="/img/mastercard.png"
                                                                alt=""
                                                            />
                                                        </li>
                                                        <li className="amex">
                                                            <img
                                                                src="/img/amex.png"
                                                                alt=""
                                                            />
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="bidder-btns mt-4">
                                        <button
                                            type="button"
                                            className="btn btn-sky back-btn"
                                            onClick={() =>
                                                setBuyerField(
                                                    "step",
                                                    BUYER_REGISTRATION_STEPS.Bidding_Limit
                                                )
                                            }
                                        >
                                            <img
                                                src="/img/arrow-left.svg"
                                                alt=""
                                                className="mr4"
                                            />
                                            {t("Back")}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary forward-btn"
                                            onClick={handleSubmit(
                                                handleProceedPayment
                                            )}
                                            disabled={!isValid}
                                        >
                                            {t("Proceed to Payment")}
                                            <img
                                                src="/img/arrow-right.svg"
                                                alt=""
                                                className="ml4"
                                            />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            ) : (
                <div className="step-3">
                    <section className="bidder-form">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-lg-8 col-md-10 col-sm-12">
                                    <div className="register-for-someone mt-0">
                                        <div
                                            className="prop-accordian accordion accordion-flush"
                                            id="accordionFlushExample"
                                        >
                                            <AccordionItem
                                                id="ownership-collapseOne"
                                                title={t("DepositDetails")}
                                            >
                                                <div className="types">
                                                    <div className="block">
                                                        <h6>
                                                            <span>
                                                                {t(
                                                                    "DepositAmont"
                                                                )}
                                                            </span>
                                                            <em className="dirham">
                                                                D{" "}
                                                                {selectedSessionID
                                                                    ? deposit_amount
                                                                    : depositAmount}
                                                            </em>
                                                        </h6>
                                                    </div>
                                                </div>
                                            </AccordionItem>
                                            <AccordionItem
                                                id="ownership-collapseTwo"
                                                title={t("SelectPaymentMethod")}
                                            >
                                                <ul>
                                                    <li>
                                                        <input
                                                            type="radio"
                                                            className="css-radio"
                                                            id="card"
                                                            {...register(
                                                                "paymentMethod",
                                                                {
                                                                    required: t(
                                                                        "PleaseChoosePaymentMethod"
                                                                    ),
                                                                }
                                                            )}
                                                            value={
                                                                Payment_Method.CARD
                                                            }
                                                        />
                                                        <label
                                                            htmlFor="card"
                                                            className="css-labell"
                                                        >
                                                            {t("Card")}
                                                        </label>
                                                    </li>
                                                    {errors.paymentMethod && (
                                                        <div className="invalid-feedback d-block">
                                                            {t(
                                                                errors
                                                                    .paymentMethod
                                                                    .message
                                                            )}
                                                        </div>
                                                    )}
                                                </ul>
                                            </AccordionItem>
                                        </div>
                                    </div>
                                    <div className="bidder-btns mb-4">
                                        <button
                                            onClick={() =>
                                                setBuyerField(
                                                    "step",
                                                    registrationType ===
                                                        RegistrationFlowType.SELF
                                                        ? BUYER_REGISTRATION_STEPS.DETAILS
                                                        : BUYER_REGISTRATION_STEPS.REVIEW
                                                )
                                            }
                                            className="btn btn-sky back-btn"
                                        >
                                            <img
                                                src="/img/arrow-left.svg"
                                                className="mr4"
                                                alt=""
                                            />
                                            {t("Back")}
                                        </button>
                                        <button
                                            disabled={!isValid}
                                            className="btn btn-primary"
                                            onClick={handleSubmit(
                                                handleProceedPayment
                                            )}
                                        >
                                            {t("ProceedToPayment")}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
};

const AccordionItem = ({ id, title, children }) => (
    <div className="accordion-item">
        <h2 className="accordion-header">
            <button
                className="accordion-button"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target={`#${id}`}
                aria-expanded="true"
                aria-controls="panelsStayOpen-collapseOne"
            >
                {title}
            </button>
        </h2>
        <div id={id} className="accordion-collapse collapse show">
            <div className="accordion-body">{children}</div>
        </div>
    </div>
);

export default RegistrationDepositPayment;
